import { createMemo, createSignal, Show, onMount, onCleanup } from 'solid-js';
import { useParams } from '@solidjs/router';
import { io } from 'socket.io-client';
import QuizQuestionPreview from '../QuizQuestionPreview';

interface LiveState {
  quizCode: string;
  quizTitle: string;
  questionIndex: number | null;
  status: 'hidden' | 'shown' | 'active' | 'stopped';
  question: any | null;
  serverNow?: number;
  receivedAt?: number;
  phaseStartedAt?: number | null;
  phaseEndsAt?: number | null;
}

interface QuestionResults {
  questionIndex: number;
  totalAnswers: number;
  answerStats: Array<{
    answer: string;
    count: number;
    percentage: number;
    isCorrect: boolean;
  }>;
}

export default function PublicLiveQuiz() {
  const params = useParams();
  const quizCode = () => (params.code ?? '').toUpperCase();

  const [state, setState] = createSignal<LiveState | null>(null);
  const [results, setResults] = createSignal<QuestionResults | null>(null);
  const [notFound, setNotFound] = createSignal(false);
  const [now, setNow] = createSignal(Date.now());

  const timer = createMemo(() => {
    const current = state();
    const startedAt = current?.phaseStartedAt;
    const endsAt = current?.phaseEndsAt;
    if (!current || (current.status !== 'shown' && current.status !== 'active') ||
      !startedAt || !endsAt || endsAt <= startedAt) return null;

    const serverNow = current.serverNow && current.receivedAt
      ? current.serverNow + (now() - current.receivedAt)
      : now();
    const secondsLeft = Math.max(0, Math.ceil((endsAt - serverNow) / 1000));
    const percentLeft = Math.max(0, Math.min(100, ((endsAt - serverNow) / (endsAt - startedAt)) * 100));
    return { secondsLeft, percentLeft };
  });

  const formattedTime = createMemo(() => {
    const seconds = timer()?.secondsLeft;
    if (seconds === undefined) return '';
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  });

  onMount(() => {
    // Create socket connection without authentication
    const newSocket = io(window.location.origin, { autoConnect: false });
    const clock = window.setInterval(() => setNow(Date.now()), 250);
    // Rejoin after reconnecting because Socket.IO rooms belong to the connection.
    newSocket.on('connect', () => newSocket.emit('public-join-quiz', { quizCode: quizCode() }));

    newSocket.on('quiz-not-found', (data) => {
      if (data.quizCode === quizCode()) {
        setNotFound(true);
      }
    });

    const handleLiveState = (s: LiveState) => {
      if (s.quizCode === quizCode()) {
        setResults(current => current?.questionIndex === s.questionIndex && s.status !== 'hidden' ? current : null);
        setState({ ...s, receivedAt: Date.now() });
        setNotFound(false);
      }
    };
    newSocket.on('live-quiz-state', handleLiveState);
    newSocket.on('live-quiz-update', handleLiveState);

    // Listen for results broadcast
    newSocket.on('public-question-results', (data: QuestionResults) => {
      setResults(data);
    });

    // Clear results when question changes
    newSocket.on('public-clear-results', () => {
      setResults(null);
    });

    newSocket.connect();

    onCleanup(() => {
      window.clearInterval(clock);
      newSocket.disconnect();
    });
  });

  return (
    <div class="container py-3">
      <Show when={!notFound()} fallback={<div class="alert alert-danger">Quiz code not found.</div>}>
        { /* <div class="text-center mb-4">
          <h2>{state()?.quizTitle || 'Loading quiz...'}</h2>
          <span class="badge bg-secondary">Public View</span>
        </div> */ }

        <Show when={state()}>
          <Show when={state()!.status !== 'hidden'} fallback={
            <div class="alert alert-info text-center">
              Waiting for the next question...
            </div>
          }>
            <Show when={timer()}>
              <div class="card border-primary shadow-sm mb-3" role="timer" aria-label={state()!.status === 'shown' ? 'Time until choices appear' : 'Time to answer'}>
                <div class="card-body text-center py-2">
                  <div class="text-muted small text-uppercase fw-semibold">
                    {state()!.status === 'shown' ? 'Choices appear in' : 'Answers close in'}
                  </div>
                  <div class="display-4 fw-bold font-monospace" aria-live="off">{formattedTime()}</div>
                </div>
                <div class="progress rounded-0" style={{ height: '8px' }}
                  role="progressbar" aria-label="Time remaining" aria-valuemin="0" aria-valuemax="100"
                  aria-valuenow={Math.round(timer()!.percentLeft)}>
                  <div class="progress-bar" classList={{ 'bg-success': state()!.status === 'active' }}
                    style={{ width: `${timer()!.percentLeft}%` }} />
                </div>
              </div>
            </Show>
            <Show when={state()!.question} fallback={<div class="alert alert-warning">Loading question...</div>}>
              {/* Results header when available 
              <Show when={results()}>
                <div class="alert alert-success mb-3">
                  <h5 class="mb-0">📊 Results from {results()!.totalAnswers} student{results()!.totalAnswers !== 1 ? 's' : ''}</h5>
                </div>
              </Show>
              */}
              {/* Show the question - recreate component when results change */}
              <Show when={!results()} fallback={
                <QuizQuestionPreview 
                  question={state()!.question} 
                  readonly={true}
                  hideCorrectAnswers={false}
                  results={results()!}
                />
              }>
                <QuizQuestionPreview 
                  question={state()!.question} 
                  readonly={true}
                  hideCorrectAnswers={true}
                  hideChoices={state()!.status === 'shown'}
                />
              </Show>

              {/* Status indicator */}
              <div class="mt-3 text-center">
                <span class="badge bg-secondary">Status: {state()!.status}</span>
                <Show when={results()}>
                  <span class="badge bg-success ms-2">Results loaded ({results()!.totalAnswers} answers)</span>
                </Show>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
